import styled from 'styled-components';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { neumorphismCard, neumorphismCardElevated, neumorphismButton } from '../../../shared/styles/neumorphism';

// ========================================
// Loading & Error States
// ========================================

export const LoadingContainer = styled.div`
  ${neumorphismCard}
  text-align: center;
  padding: 2rem;
  background: white;
`;

export const ErrorContainer = styled.div`
  ${neumorphismCard}
  text-align: center;
  padding: 2rem;
  background: white;
  border-left: 4px solid #e1562d;
`;

export const ActionButton = styled.button`
  ${neumorphismButton}
  background: #667eea;
  color: white;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  margin-top: 1rem;

  &:hover {
    background: #5a67d8;
  }
`;

// ========================================
// Question Card
// ========================================

export const QuestionCard = styled.div`
  ${neumorphismCardElevated}
  background: white;
  overflow: hidden;
  min-width: 600px;
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    min-width: 300px;
    max-width: 90%;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 2rem;
`;

export const SubcategoryImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const SubcategoryImage = styled.img`
  max-width: 180px;
  height: auto;
  object-fit: contain;
  
  @media (max-width: 768px) {
    max-width: 140px;
  }
`;

export const SubcategoryIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  color: #667eea;
`;

export const SubcategoryTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #667eea;
`;

// ========================================
// Question Section
// ========================================

export const QuestionSection = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #eee;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #667eea;
  font-size: 1.2rem;
`;

export const QuestionText = styled.p`
  font-size: 1.3rem;
  line-height: 1.6;
  color: #333;
  margin: 0;
`;

// ========================================
// Answer Section
// ========================================

export const AnswerSection = styled.div`
  padding: 2rem;
  background: #f8f9ff;
`;

export const AnswerSectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #43e97b;
  font-size: 1.2rem;
`;

export const AnswerText = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  color: #333;
  margin: 0 0 1.5rem 0;
  font-weight: 500;
`;

// ========================================
// Options Section
// ========================================

export const OptionsSection = styled.div`
  padding: 2rem;
  background: #f8f9ff;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
`;

export const OptionsSectionTitle = styled.h3`
  margin: 0 0 1.5rem 0;
  color: #667eea;
  font-size: 1.2rem;
  font-weight: 600;
`;

export const OptionsGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  
  /* Use 2 columns for multiple-choice questions (Sabiduria) on larger screens */
  ${props => props.useTwoColumns && `
    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
  `}
  
  /* Always use 2 columns for true-false questions (short options) */
  ${props => props.isTrueFalse && `
    grid-template-columns: repeat(2, 1fr);
  `}
`;

export const OptionCard = styled.div`
  ${neumorphismCard}
  padding: 1.2rem;
  position: relative;
  cursor: default;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      8px 8px 16px rgba(102, 126, 234, 0.1),
      -8px -8px 16px rgba(255, 255, 255, 0.8);
  }
  
  ${props => props.isCorrect && props.showAnswer && `
    border: 2px solid #43e97b;
    background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
    transform: translateY(-2px);
    box-shadow: 
      8px 8px 20px rgba(67, 233, 123, 0.2),
      -8px -8px 20px rgba(255, 255, 255, 0.9);
  `}
`;

export const OptionLabel = styled.div`
  position: absolute;
  top: -8px;
  left: 12px;
  background: ${props => props.isCorrect && props.showAnswer ? '#43e97b' : '#667eea'};
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.8rem;
  height: 1.8rem;
`;

export const CorrectIcon = styled(IoCheckmarkCircle)`
  position: absolute;
  top: -8px;
  right: -8px;
  font-size: 1.5rem;
  color: #43e97b;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

export const OptionText = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 1.1rem;
  line-height: 1.5;
  color: #333;
  font-weight: 500;
`;

export const ReferenceText = styled.p`
  font-size: 1rem;
  color: #666;
  font-style: italic;
  margin: 1rem 0 0 0;
  padding-top: 1rem;
  border-top: 1px solid #eee;
`;

// ========================================
// Actions Container & Buttons
// ========================================

export const ActionsContainer = styled.div`
  padding: 2rem;
  text-align: center;
`;

export const ShowAnswerButton = styled.button`
  ${neumorphismButton}
  background: #667eea;
  color: white;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  cursor: pointer;

  &:hover {
    background: #5a67d8;
  }
`;

export const EndTurnButton = styled.button`
  ${neumorphismButton}
  background: #e1562d;
  color: white;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  cursor: pointer;

  &:hover {
    background: #e1562d;
  }
`;

